export interface CashbackRules {
  type: 'percent' | 'fixed' | 'tiered';
  default_percent: number;
  fixed_amount: number;
  min_purchase: number;
  max_cashback_per_purchase: number;
  validity_days: number;
  first_purchase_bonus_percent?: number;
  birthday_bonus_percent?: number;
  vip_bonus_percent?: number;
  specific_days_enabled?: boolean;
  active_days?: string[]; // e.g. ['seg', 'ter', 'qua']
  category_rules?: Array<{ category: string; percent: number }>;
  staged_release_enabled?: boolean;
  staged_stages?: Array<{ days_after: number; percent_of_cashback: number }>;
  use_min_purchase?: number;
  use_max_percent_per_sale?: number; // e.g. max 30% of total bill can be paid with cashback
}

export interface CashbackCompany {
  id: string;
  name: string;
  category: string;
  logo_url: string;
  banner_url?: string;
  city_state?: string;
  phone?: string;
  whatsapp?: string;
  status: 'active' | 'blocked' | 'pending';
  plan: 'free' | 'pro' | 'enterprise';
  cross_cashback_enabled: boolean;
  settings: CashbackRules;
  created_at: string;
}

export interface CashbackRequest {
  id: string;
  business_id: string;
  business_name: string;
  user_id: string;
  user_name: string;
  user_phone: string;
  receipt_photo: string;
  receipt_number: string;
  purchase_amount: number;
  purchase_date: string;
  calculated_cashback: number;
  status: 'pending' | 'approved' | 'rejected' | 'reupload_requested';
  rejection_reason?: string;
  created_at: string;
  reviewed_at?: string;
}

export interface CashbackTransaction {
  id: string;
  business_id: string;
  business_name: string;
  type: 'earn' | 'spend' | 'expire' | 'bonus' | 'referral';
  amount: number;
  description: string;
  date: string;
  status: 'active' | 'used' | 'expired' | 'pending';
  expires_at?: string;
}

export interface CashbackWallet {
  user_id: string;
  user_name: string;
  user_email: string;
  available_balance: number;
  pending_balance: number;
  used_balance: number;
  expired_balance: number;
  transactions: CashbackTransaction[];
}

export interface CashbackCampaign {
  id: string;
  business_id: string;
  business_name: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  cashback_percent: number;
  fixed_bonus: number;
  status: 'active' | 'scheduled' | 'finished';
  target_audience: 'all' | 'first_time' | 'vip' | 'birthday';
}

export interface CashbackMission {
  id: string;
  title: string;
  description: string;
  reward_cashback: number;
  target_count: number;
  current_count: number;
  completed: boolean;
  type: 'buy_times' | 'spend_total' | 'different_stores';
}

export interface CashbackReferral {
  referral_code: string;
  total_referred: number;
  total_earned: number;
  referrer_bonus: number;
  referred_bonus: number;
}

export interface CashbackBannerConfig {
  badge_text: string;
  title: string;
  subtitle: string;
  banner_image_url: string;
  cta_button_text: string;
  cta_button_action: 'wallet' | 'register' | 'directory';
}

export const INITIAL_CASHBACK_BANNER_CONFIG: CashbackBannerConfig = {
  badge_text: 'Clube de Cashback Multi-Empresas',
  title: 'Receba Dinheiro de Volta em Cada Compra',
  subtitle: 'Envie a nota fiscal das suas compras nos estabelecimentos parceiros e acumule saldo real na sua carteira digital para resgatar onde quiser!',
  banner_image_url: '/images/happy_cashback_banner.jpg',
  cta_button_text: 'Abrir Minha Carteira & Enviar Nota',
  cta_button_action: 'wallet'
};

export interface CashbackMerchantAccessRequest {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  company_name: string;
  company_category: string;
  company_city: string;
  cnpj_cpf?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  reviewed_at?: string;
}

export const INITIAL_CASHBACK_MERCHANT_REQUESTS: CashbackMerchantAccessRequest[] = [
  {
    id: 'req-m-1',
    user_id: 'usr-3',
    user_name: 'Roberto Souza',
    user_email: 'roberto@padariaferro.com.br',
    user_phone: '(16) 99123-4567',
    company_name: 'Empório & Conveniência Souza',
    company_category: 'Supermercado & Conveniência',
    company_city: 'Ribeirão Preto - SP',
    cnpj_cpf: '12.345.678/0001-90',
    notes: 'Gostaria de oferecer cashback aos meus clientes.',
    status: 'pending',
    created_at: '2026-07-30 11:20'
  }
];

// Initial Mock Seed Data for Demonstration & Storage Fallback
export const INITIAL_CASHBACK_COMPANIES: CashbackCompany[] = [
  {
    id: 'emp-1',
    name: 'Padaria e Confeitaria Ferro',
    category: 'Padaria & Confeitaria',
    logo_url: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=200&h=200&fit=crop',
    banner_url: 'https://images.unsplash.com/photo-1517433670267-08bbd4be890f?w=1000&h=400&fit=crop',
    city_state: 'Ribeirão Preto - SP',
    phone: '(16) 99209-1604',
    whatsapp: '5516992091604',
    status: 'active',
    plan: 'pro',
    cross_cashback_enabled: true,
    created_at: '2026-01-10',
    settings: {
      type: 'percent',
      default_percent: 10,
      fixed_amount: 0,
      min_purchase: 20,
      max_cashback_per_purchase: 50,
      validity_days: 60,
      first_purchase_bonus_percent: 15,
      birthday_bonus_percent: 20,
      vip_bonus_percent: 12,
      specific_days_enabled: false,
      staged_release_enabled: false,
      use_min_purchase: 10,
      use_max_percent_per_sale: 50
    }
  },
  {
    id: 'emp-2',
    name: 'Auto Posto Cidade',
    category: 'Posto de Combustível',
    logo_url: 'https://images.unsplash.com/photo-1527018601619-a508a2be00d6?w=200&h=200&fit=crop',
    banner_url: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1000&h=400&fit=crop',
    city_state: 'Ribeirão Preto - SP',
    phone: '(16) 3600-1122',
    whatsapp: '5516992091604',
    status: 'active',
    plan: 'enterprise',
    cross_cashback_enabled: true,
    created_at: '2026-01-15',
    settings: {
      type: 'percent',
      default_percent: 5,
      fixed_amount: 0,
      min_purchase: 50,
      max_cashback_per_purchase: 30,
      validity_days: 30,
      first_purchase_bonus_percent: 10,
      birthday_bonus_percent: 10,
      use_min_purchase: 15,
      use_max_percent_per_sale: 30
    }
  },
  {
    id: 'emp-3',
    name: 'Academia Fit360',
    category: 'Academias & Fitness',
    logo_url: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=200&h=200&fit=crop',
    banner_url: 'https://images.unsplash.com/photo-1517838277536-f5f99be501cd?w=1000&h=400&fit=crop',
    city_state: 'São Paulo - SP',
    phone: '(11) 98888-2211',
    whatsapp: '5511988882211',
    status: 'active',
    plan: 'pro',
    cross_cashback_enabled: true,
    created_at: '2026-02-01',
    settings: {
      type: 'percent',
      default_percent: 8,
      fixed_amount: 0,
      min_purchase: 100,
      max_cashback_per_purchase: 80,
      validity_days: 90,
      first_purchase_bonus_percent: 20,
      birthday_bonus_percent: 25,
      use_min_purchase: 20,
      use_max_percent_per_sale: 40
    }
  },
  {
    id: 'emp-4',
    name: 'Farmácia Saúde Total',
    category: 'Farmácias & Drogaria',
    logo_url: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&h=200&fit=crop',
    banner_url: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=1000&h=400&fit=crop',
    city_state: 'Ribeirão Preto - SP',
    phone: '(16) 3999-0011',
    whatsapp: '5516992091604',
    status: 'active',
    plan: 'free',
    cross_cashback_enabled: false,
    created_at: '2026-02-10',
    settings: {
      type: 'percent',
      default_percent: 7,
      fixed_amount: 0,
      min_purchase: 30,
      max_cashback_per_purchase: 40,
      validity_days: 45,
      use_min_purchase: 10,
      use_max_percent_per_sale: 50
    }
  }
];

export const INITIAL_CASHBACK_REQUESTS: CashbackRequest[] = [
  {
    id: 'req-101',
    business_id: 'emp-1',
    business_name: 'Padaria e Confeitaria Ferro',
    user_id: 'usr-1',
    user_name: 'Carlos Eduardo Silva',
    user_phone: '16992001122',
    receipt_photo: 'https://images.unsplash.com/photo-1554415707-6e8cfc93fe23?w=600&h=800&fit=crop',
    receipt_number: 'NF-884920',
    purchase_amount: 150.00,
    purchase_date: '2026-07-28',
    calculated_cashback: 15.00,
    status: 'pending',
    created_at: '2026-07-28 14:30'
  },
  {
    id: 'req-102',
    business_id: 'emp-2',
    business_name: 'Auto Posto Cidade',
    user_id: 'usr-1',
    user_name: 'Carlos Eduardo Silva',
    user_phone: '16992001122',
    receipt_photo: 'https://images.unsplash.com/photo-1554415707-6e8cfc93fe23?w=600&h=800&fit=crop',
    receipt_number: 'NF-112034',
    purchase_amount: 250.00,
    purchase_date: '2026-07-25',
    calculated_cashback: 12.50,
    status: 'approved',
    created_at: '2026-07-25 10:15',
    reviewed_at: '2026-07-25 11:00'
  },
  {
    id: 'req-103',
    business_id: 'emp-1',
    business_name: 'Padaria e Confeitaria Ferro',
    user_id: 'usr-2',
    user_name: 'Fernanda Lima',
    user_phone: '16981112233',
    receipt_photo: 'https://images.unsplash.com/photo-1554415707-6e8cfc93fe23?w=600&h=800&fit=crop',
    receipt_number: 'NF-992011',
    purchase_amount: 85.00,
    purchase_date: '2026-07-29',
    calculated_cashback: 8.50,
    status: 'pending',
    created_at: '2026-07-29 09:20'
  }
];

export const INITIAL_CASHBACK_TRANSACTIONS: CashbackTransaction[] = [
  {
    id: 'tx-1',
    business_id: 'emp-2',
    business_name: 'Auto Posto Cidade',
    type: 'earn',
    amount: 12.50,
    description: 'Cashback aprovado - NF-112034',
    date: '2026-07-25',
    status: 'active',
    expires_at: '2026-08-25'
  },
  {
    id: 'tx-2',
    business_id: 'emp-1',
    business_name: 'Padaria e Confeitaria Ferro',
    type: 'bonus',
    amount: 10.00,
    description: 'Bônus Boas-Vindas Novo Membro',
    date: '2026-07-20',
    status: 'active',
    expires_at: '2026-08-05' // Preste a expirar!
  },
  {
    id: 'tx-3',
    business_id: 'emp-3',
    business_name: 'Academia Fit360',
    type: 'spend',
    amount: -15.00,
    description: 'Resgate utilizado na mensalidade',
    date: '2026-07-15',
    status: 'used'
  }
];

export const INITIAL_CASHBACK_CAMPAIGNS: CashbackCampaign[] = [
  {
    id: 'cmp-1',
    business_id: 'emp-1',
    business_name: 'Padaria e Confeitaria Ferro',
    title: '🔥 Fim de Semana em Dobro - 20% Cashback',
    description: 'Faça qualquer compra no sábado ou domingo acima de R$ 30 e receba 20% de volta na sua carteira!',
    start_date: '2026-07-01',
    end_date: '2026-08-31',
    cashback_percent: 20,
    fixed_bonus: 0,
    status: 'active',
    target_audience: 'all'
  },
  {
    id: 'cmp-2',
    business_id: 'emp-2',
    business_name: 'Auto Posto Cidade',
    title: '⛽ Abasteça R$ 200 e Ganhe R$ 15 de Bônus Fixo',
    description: 'Gasolina aditivada com bônus direto em dinheiro para a próxima abastecida.',
    start_date: '2026-07-10',
    end_date: '2026-08-15',
    cashback_percent: 7.5,
    fixed_bonus: 15,
    status: 'active',
    target_audience: 'all'
  },
  {
    id: 'cmp-3',
    business_id: 'emp-3',
    business_name: 'Academia Fit360',
    title: '🎂 Mês do Aniversariante - 25% de Cashback Especial',
    description: 'Aniversariantes do mês ganham 25% de retorno em suplementos e produtos parceiros.',
    start_date: '2026-07-01',
    end_date: '2026-12-31',
    cashback_percent: 25,
    fixed_bonus: 0,
    status: 'active',
    target_audience: 'birthday'
  }
];

export const INITIAL_CASHBACK_MISSIONS: CashbackMission[] = [
  {
    id: 'mis-1',
    title: '🛍️ Explorador do Bairro',
    description: 'Compre e envie nota fiscal de 3 empresas diferentes parceiras do sistema.',
    reward_cashback: 15.00,
    target_count: 3,
    current_count: 2,
    completed: false,
    type: 'different_stores'
  },
  {
    id: 'mis-2',
    title: '🥐 Fã da Padaria Ferro',
    description: 'Envie 3 notas fiscais de compras na Padaria e Confeitaria Ferro este mês.',
    reward_cashback: 10.00,
    target_count: 3,
    current_count: 1,
    completed: false,
    type: 'buy_times'
  },
  {
    id: 'mis-3',
    title: '💰 Economizador Master',
    description: 'Acumule um valor total de R$ 300 em compras registradas com cashback.',
    reward_cashback: 25.00,
    target_count: 300,
    current_count: 235,
    completed: false,
    type: 'spend_total'
  }
];
